/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import SymbolData from '../Symbol/SymbolData';
import AnimationManager from '../Utility/Component/Animation/AnimationManager';
import Reel from './Reel';

/** Represents a single symbol on a [[Reel]] face. */
@cc._decorator.ccclass
export default class Symbol extends cc.Component
{
    private _animationManagers: AnimationManager[] = [];

    /** This symbol's position on the reel strip. */
    private _reelPosition: number = 0;
    /** Accessor for the symbol's position on the reel strip. */
    public get ReelPosition(): number { return this._reelPosition; }
    /** Mutator for the symbol's position on the reel strip. */
    public set ReelPosition(value: number) { this._reelPosition = value; }

    /** The height of this symbol that its width is scaled to. */
    private _symbolHeight: number = 0;
    /** Mutator for the symbol's height which will scale the width to maintain aspect ratio. */
    public set SymbolHeight(value: number) { this._symbolHeight = value; }

    /** The [[Reel]] whose face this symbol is associated with. */
    private _reel: Reel = null;
    /** Mutator for which [[Reel]] face this symbol is associated with. */
    public set Reel(value: Reel) { this._reel = value; }

    /** The associated data for this symbol. */
    private _symbolData: SymbolData = null;
    /** Accessor for the data associated with this symbol. */
    public get SymbolData(): SymbolData { return this._symbolData; }
    /** Mutator for the data associated with this symbol. */
    public set SymbolData(value: SymbolData) { this._symbolData = value; }

    /** This symbol's visible offset on the [[Reel]] face. */
    private _offSet: number = 0;
    /** Accessor for this symbol's visible offset on the [[Reel]] face. */
    public get OffSet(): number { return this._offSet; }
    /** Mutator for this symbol's visible offset on the [[Reel]] face. */
    public set OffSet(value: number) { this._offSet = value; }

    /** Is this symbol currently visible on the [[Reel]] face. */
    private _isReelFace: boolean = true;
    /** Accessor for if this symbol is currently visible on the [[Reel]] face. */
    public get IsReelFace(): boolean { return this._isReelFace; }
    /** Mutator for if this symbol is currently visible on the [[Reel]] face. */
    public set IsReelFace(value: boolean) { this._isReelFace = value; }

    private _activeAnimationManager: AnimationManager = null;

    /** Sets absolute Y-axis position once the reel has stopped. */
    public UpdateStopped(): void
    {
        if (this._reel.StopSymbolIndex === -1) { return; }

        const myIndex: number = this._reel.GetVisibleSymbolIndex(this.node);
        const indexOffset: number = myIndex - this._reel.StopSymbolIndex;
        const finalPosition: number = this._reel.RootOffset + (indexOffset * this._reel.SymbolHeight);

        if (this.node.y !== finalPosition)
        {
            this.node.y = finalPosition;
        }
    }

    /**
     * Updates the symbol's Y-axis position based on the reel's current position.
     * @param reelPositionPx The [[Reel]]'s current position, in pixels.
     */
    public UpdateReelPosition(reelPositionPx: number): void
    {
        const visibleOffset: number = this._reel.GetVisibleSymbolIndex(this.node);
        const visibleOffsetPx: number = visibleOffset * this._reel.SymbolHeight;
        this.node.y = visibleOffsetPx - reelPositionPx;
    }

    public GetPaylineMaxDuration(): number
    {
        return this._activeAnimationManager
            ? this._activeAnimationManager.MaxPaylineDuration
            : 0;
    }

    public AddAnimationManager(manager: AnimationManager): void
    {
        this.node.addChild(manager.node);
        this._animationManagers.push(manager);
    }

    public SetActive(symbolId: string): void
    {
        this._animationManagers.forEach(manager =>
            {
                if (manager.SetActive(symbolId))
                {
                    this._activeAnimationManager = manager;
                }
            });
    }
}
